import { HttpRequest } from '@angular/common/http';
import { AuthenticationService } from '@core/services/authentication/authentication.service';
import { environment } from '@env/environment';
import { SESSION_KEYS } from '@feature/auth/constants/auth.constant';

// Route patterns for different authentication types
const BASIC_AUTH_ROUTES = [
  '/partner/adminLogin',
  '/partner/adminVerifyCode',
  '/partner/adminforgotPassword',
  '/partner/adminforgotPassword/verifyCode',
  // Public self-service account-deletion endpoints (no JWT / no logged-in user).
  // They authenticate with partner Basic Auth, same as login.
  '/sender/accountDeletion/requestCode',
  '/sender/accountDeletion/confirm',
] as const;

const RESET_PASSWORD_ROUTES = [
  '/partner/adminforgotPassword/newPassword',
] as const;

const PUBLIC_ROUTES = [] as const;

/**
 * Checks if the given URL requires basic authentication
 * @param url - The request URL to check
 * @returns true if the route requires basic auth, false otherwise
 */
function isBasicAuthRoute(url: string): boolean {
  return BASIC_AUTH_ROUTES.some(route => url.endsWith(route));
}

/**
 * Checks if the given URL is a password reset route
 * @param url - The request URL to check
 * @returns true if the route is for password reset, false otherwise
 */
function isResetPasswordRoute(url: string): boolean {
  return RESET_PASSWORD_ROUTES.some(route => url.endsWith(route));
}

/**
 * Checks if the given URL is a public route that doesn't require authentication
 * @param url - The request URL to check
 * @returns true if the route is public, false otherwise
 */
export function isPublicRoute(url: string): boolean {
  return PUBLIC_ROUTES.some(route => url.includes(route));
}

/**
 * Creates an authenticated request based on the route type
 */
export function createAuthenticatedRequest(
  req: HttpRequest<unknown>,
  authService: AuthenticationService
): HttpRequest<unknown> {
  if (isBasicAuthRoute(req.url)) {
    return createBasicAuthRequest(req);
  }

  if (isResetPasswordRoute(req.url)) {
    return createResetPasswordRequest(req);
  }

  return createBearerTokenRequest(req, authService);
}

/**
 * Creates request with Basic Authentication
 */
function createBasicAuthRequest(
  req: HttpRequest<unknown>
): HttpRequest<unknown> {
  const basicAuth = btoa(
    `${environment.userName}:${environment.partnerAPIToken}`
  );

  return req.clone({
    setHeaders: {
      Authorization: `Basic ${basicAuth}`,
    },
  });
}

/**
 * Creates request with RP token for password reset
 */
function createResetPasswordRequest(
  req: HttpRequest<unknown>
): HttpRequest<unknown> {
  const rpToken = getStoredRPToken();

  if (!rpToken) {
    console.warn('RP token not found for reset password request');
    return req;
  }

  return req.clone({
    setHeaders: {
      Authorization: `Bearer ${rpToken}`,
    },
  });
}

/**
 * Creates request with JWT Bearer token
 */
function createBearerTokenRequest(
  req: HttpRequest<unknown>,
  authService: AuthenticationService
): HttpRequest<unknown> {
  const token = authService.getToken();

  if (!token) {
    console.warn('JWT token not available for authenticated request');
    return req;
  }

  return req.clone({
    setHeaders: {
      Authorization: `Bearer ${token}`,
    },
  });
}

/**
 * Safely retrieves the reset-password token.
 *
 * Reads sessionStorage, which is where verify-otp puts it. This used to read
 * localStorage against a duplicated 'xbpRPToken' literal, so the token was
 * never found and adminforgotPassword/newPassword went out with no
 * Authorization header - a 401 at the last step of every password reset. The
 * shared key constant is used now so the two sides cannot drift again.
 */
function getStoredRPToken(): string | null {
  try {
    return sessionStorage.getItem(SESSION_KEYS.RESET_PASSWORD_TOKEN);
  } catch (error) {
    console.error('Error accessing sessionStorage for RP token:', error);
    return null;
  }
}
